import {
  memo,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Activity,
  AppWindow,
  ArrowUpRight,
  Bot,
  CircleCheckBig,
  File,
  Focus,
  Globe,
  LayoutGrid,
  Mail,
  MessageSquare,
  MessageSquareText,
  Minus,
  Plus,
  Repeat2,
  Scan,
  Smartphone,
  TrendingUp,
} from 'lucide-react';
import { agentDotShapes, colorForAgent } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { ACTIVITY_MAP_BI_PAGE, activityMapNodeClick } from '../../../../lib/activity-map-bi';
import { connectorLogoUrl } from '../../../../lib/connectors';
import {
  AGENT_RING_R,
  BRIEF_CARD,
  BRIEF_ID,
  MAESTRO_ID,
  WORLD_CENTER,
  WORLD_H,
  WORLD_W,
  edgePath,
  regionRect,
  type BucketId,
  type MapEdge,
  type MapNode,
  type MapWorld,
  SPACES_Y,
} from '../../../../lib/home-map/map-layout';
import {
  ENTRY_WORLD_HOLD_MS,
  ENTRY_ZOOM_MS,
  clamp,
  easeInOut,
  frame,
  frameWorld,
  lerpView,
  pan,
  toSvgViewBox,
  toWorld,
  zoomAbout,
  type ViewBox,
} from '../../../../lib/home-map/map-camera';
import { bucketOf, type MapEvent } from '../../../../lib/home-map/map-events';
import type { Flight } from '../../../../lib/home-map/use-map-live';
import { Card, IconButton, Typography } from '../../ui';
import { MaestroMark } from '../../MaestroMark';
// The canvas draws the Maestro tile to its own frame's geometry but wears the shared mark's fills — the same
// near-black tile and white shapes, and the same dark-mode inversion — so the two marks cannot drift apart.
import maestroMarkStyles from '../../MaestroMark.module.css';
import { OutreachChannelGlyph } from '../../connectors/OutreachChannelGlyph';
import { WixWordmark } from '../../connectors/BrandGlyphs';
import { outreachLogoUrl } from '../../../../lib/home-map/channels';
import {
  CANVAS_AGENTS,
  CANVAS_CHANNEL,
  CANVAS_CONNECTORS,
  CANVAS_CONNECTORS_LINE,
  CANVAS_DOCK_ANCHOR,
  CANVAS_DOT,
  CANVAS_ENTRY_DOT_FROM_END,
  CANVAS_ENTRY_LINES,
  CANVAS_FIGMA_SCALE,
  CANVAS_MAESTRO,
  canvasChannelLines,
  canvasDefaultView,
  canvasEntryPath,
  canvasGraphBounds,
  canvasGround,
  canvasEntryPoints,
  canvasPan,
  canvasZoom,
  canvasZoomAbout,
  canvasZoomStep,
  type CanvasLine,
} from '../../../../lib/home-map/canvas-layout';
import { WhatsAppCanvasGlyph } from '../../connectors/BrandGlyphs';
import { MapInfoCard, type MapCardContent } from './MapInfoCard';
import { CanvasDemoFlow, type DemoFlowCategory, type DemoFlowChannel, type DemoFlowGeo } from './CanvasDemoFlow';
import type { BadgeFill } from '../../../../lib/home-map/phone-flow';
import { HOME3_CANVAS_LABEL_KEY, type Home3Destination } from '../../../../lib/home3-feed';
import styles from './HomeMap.module.css';

/**
 * The map canvas — ONE `<svg>` holding the static scene (guide ring, spokes, nodes) and the particles in
 * flight, with the controls in a bar along its foot and the info card floated over it.
 *
 * Performance contract (docs/claude/home2-map.md): the scene is laid out once per `world` identity; the
 * camera is a `viewBox` string that only changes while the user pans/zooms or during the 900 ms entry tween
 * (a rAF loop that STOPS when it lands); every particle is a CSS animation along an `offset-path` whose
 * delay is fixed when it mounts, so a whole reel is one render; the spokes' travelling pulses are infinite
 * CSS animations that cost no React work. The line language is the Connectors & MCP genome's: 1px ink
 * spokes with a signal riding them, dotted grey for an unconnected channel.
 */

export const BUCKET_ICONS: Record<BucketId, typeof File> = {
  tasks: CircleCheckBig,
  goals: TrendingUp,
  routines: Repeat2,
  files: File,
  apps: LayoutGrid,
  messages: Mail,
};

/** What the reader picked on the map: a node, a wire, or a particle (an event). */
export type MapSelection = { kind: 'node'; id: string } | { kind: 'edge'; from: string; to: string } | { kind: 'event'; event: MapEvent };

export interface HomeMapProps {
  world: MapWorld;
  flights: Flight[];
  settle: (id: string) => void;
  /** Where the entry choreography lands: the brief (home), the agents ring (the Agents destination), or the
   *  whole world (a map with no brief card on it — the canvas). */
  landOn: 'brief' | 'agents' | 'world';
  /** Drawn as a full-bleed CANVAS (the Overview home's map view on the desktop) rather than as a card: no
   *  radius and no card fill, and no band reserved under the scene — the dotted ground runs to every edge
   *  and the controls float over it. Not a DS Card on purpose: a surface that IS the screen is not a card. */
  bleed?: boolean;
  /** The SMALL state: a section is open under the map, so the map is a short, static band — no controls,
   *  no cards; any click on it goes back to the LARGE state (see `onGroundClick`). */
  compact?: boolean;
  /** A district was clicked: open that Space (the caller navigates). */
  onOpenSpace?: (node: MapNode) => void;
  /** The main agent's name — what the centre node is called (never a hard-coded "Maestro"). */
  maestroName?: string;
  /** An event picked from OUTSIDE the map (a timeline tick): its card opens as if its particle were clicked. */
  picked?: { event: MapEvent; seq: number } | null;
  /** A click on the map's empty ground (or, in the small state, anywhere on it). */
  onGroundClick?: () => void;
  /** The timeline (activity) button. */
  onTimeline: () => void;
  /** A particle just landed on a bucket — the tile row bumps that tile. */
  onLand?: (bucket: BucketId) => void;
  /** What the info card says for a selection: title, region, text, action. Null = nothing to show. */
  describe: (selection: MapSelection) => MapCardContent | null;
  /** The morning meeting card, rendered ON the map above the ring (see MapBriefCard). Absent = no card. */
  briefCard?: React.ReactNode;
  /** The timeline (play · track · live), rendered in the map's foot beside the control bar. */
  timeline?: React.ReactNode;
  /** A count on a node's corner (replies waiting on the reader, per outreach channel), by node id. */
  badges?: Record<string, number>;
  /**
   * The canvas's SCREEN layer, above the dock: where the entry-point lines and the particles running them
   * are drawn, because their far end is pinned to the entry points (screen furniture) while their near end
   * moves with the camera. Only meaningful with `bleed`.
   */
  entryLayer?: HTMLElement | null;
  /**
   * Where the canvas's zoom pill goes: the host's player band (`Home3Player`), screen furniture the camera
   * never moves. The pill is portalled into it, so it stays wired to this map's camera. Only meaningful with
   * `bleed`; without a slot the pill is not drawn.
   */
  zoomSlot?: HTMLElement | null;
  /**
   * Where the canvas's demo loop draws (`CanvasDemoFlow`): the host's layer over the map and UNDER its dock,
   * so the entry points stay above what streams into them. Only meaningful with `bleed`.
   */
  demoLayer?: HTMLElement | null;
  /**
   * The canvas's TOP layer, over everything the host draws on the canvas — its chat bar included — where the
   * map's cards (an agent's, a connector's) open, so no part of the canvas can cover a card. Only meaningful
   * with `bleed`; without it the card opens on `entryLayer`.
   */
  popoverLayer?: HTMLElement | null;
  /** The canvas's player is paused: the demo loop and the orbit hold still where they are. */
  paused?: boolean;
  /** The canvas's entry badges, filled by the demo loop (`CanvasDemoFlow`'s `fill`). Only meaningful with `bleed`. */
  demoFill?: BadgeFill<string>;
  /** The demo loop moved `demoFill`. */
  onDemoFill?: () => void;
}

const NODE_R = { maestro: 24, agent: 12, small: 16, bucket: 16 } as const;
/** Below this host width the minimap is dropped (it would sit over the connectors row). */
/** A district plate: wide enough for an icon and a Space's name. */
const DISTRICT = { w: 168, h: 44 };
const MINIMAP_MIN_W = 600;
/** Labels sit this far under a node's edge, so a label never runs into the glyph above it. */
const LABEL_GAP = 16;
/** The arrival choreography, in the Connectors & MCP genome's timing: the hub first, then every node flies
 *  out of it in band order (74 ms apart), each spoke drawing itself as its node lands, the signals starting
 *  once the wire is there. */
const FLY_BASE_MS = 180;
const FLY_STAGGER_MS = 74;
const FLY_MS = 720;
const PULSE_STAGGER_MS = 300;

/** A node's place in the arrival stagger: the hub first, then the ring, then the bands top to bottom. */
function orderOf(node: MapNode): number {
  switch (node.region) {
    case 'centre':
      return 0;
    case 'brief':
      return 1;
    case 'agents':
      return 1 + Math.round(Math.abs(node.x - WORLD_CENTER.x) / 40);
    case 'channelsIn':
    case 'outreach':
      return 3 + Math.round(Math.abs(node.y - WORLD_CENTER.y) / 60);
    case 'connectors':
      return 5 + Math.round(Math.abs(node.x - WORLD_CENTER.x) / 120);
    default:
      return 8;
  }
}

function nodeById(world: MapWorld): Record<string, MapNode> {
  return Object.fromEntries(world.nodes.map((n) => [n.id, n]));
}

export function HomeMap({
  world,
  flights,
  settle,
  landOn,
  compact = false,
  bleed = false,
  onGroundClick,
  onTimeline,
  onLand,
  onOpenSpace,
  describe,
  briefCard,
  timeline,
  badges,
  picked,
  maestroName,
  entryLayer = null,
  zoomSlot = null,
  demoLayer = null,
  popoverLayer = null,
  paused = false,
  demoFill,
  onDemoFill,
}: HomeMapProps) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [viewport, setViewport] = useState({ w: 0, h: 0 });
  const [view, setView] = useState<ViewBox>(() => frameWorld({ w: 1200, h: 600 }));
  const viewRef = useRef(view);
  viewRef.current = view;
  const [selected, setSelected] = useState<MapSelection | null>(null);
  const [entered, setEntered] = useState(false);
  // A particle that just landed pops its destination: a ripple keyed per landing, so it always replays.
  const [landed, setLanded] = useState<Record<string, number>>({});
  const reducedMotion = useReducedMotion();
  const nodes = useMemo(() => nodeById(world), [world]);
  // The canvas's channel lines, by channel node id — drawn under the plates and run by inbound particles.
  const channelLines = useMemo(() => (bleed ? canvasChannelLines(world.nodes) : []), [bleed, world]);
  // Where the dock is (measured by `syncDock` below), and from it the entry lines' geometry, for both of their
  // halves (under the agents in the map, over the column on top).
  const [entryAnchors, setEntryAnchors] = useState<EntryAnchors | null>(null);
  const entryLayerRef = useRef<HTMLElement | null>(null);
  entryLayerRef.current = bleed ? entryLayer : null;
  /** Where the octopus rests, in host px, as last measured (`measureFreeCentre`). */
  const freeCentreRef = useRef<{ x: number; y: number } | null>(null);
  /** The camera's resting view as last set (landing, Focus), so a resize knows whether the reader has moved it. */
  const restRef = useRef<ViewBox | null>(null);
  /** The column's laid-out size and the world's nodes, for the octopus's box (`canvasGraphBounds`). */
  const columnRef = useRef<{ w: number; h: number } | null>(null);
  const worldNodesRef = useRef(world.nodes);
  worldNodesRef.current = world.nodes;
  /** Whether the camera still stands where it was last rested — the reader has not panned or zoomed it. */
  // Set by the reader's own camera moves (drag, pinch, wheel, double-click, the zoom buttons), cleared by Focus.
  // "At rest" is that flag, not a comparison with the last resting view: anything the code does to the camera
  // in between (a re-fit, a re-centre on a stale size) would otherwise break the comparison and leave the
  // octopus wherever a half-settled first layout put it.
  const userMovedRef = useRef(false);
  const atRest = (_v?: ViewBox) => !!restRef.current && !userMovedRef.current;
  /** `restView`, for `syncDock`, which is declared before it. */
  const restViewRef = useRef<(vp: { w: number; h: number }) => ViewBox>(() => ({ x: 0, y: 0, w: 1, h: 1 }));
  const syncDockRef = useRef<(() => void) | null>(null);
  /** The scale last written onto the dock, so a re-measure can read its layout through it. */
  const dockScaleRef = useRef(1);
  const entryGeo = entryAnchors && viewport.w > 0 ? entryLineGeometry(entryAnchors, view, viewport) : null;
  // The entry column is part of the graph: laid out by its host at the frame's size where the dock rests, it is
  // moved and scaled here onto its place in the world — in the same commit as the camera, so it never lags the
  // lines that land on it. The Maestro's spot is published beside it for what is anchored to the octopus (the
  // brief popover, `.canvasBrief`).
  useLayoutEffect(() => {
    const scope = entryLayer?.parentElement;
    const dock = scope?.querySelector<HTMLElement>('[data-entry-column]')?.parentElement;
    if (!bleed || !scope || !dock || !entryGeo) return;
    const { anchors, dock: at, s, project } = entryGeo;
    dock.style.transformOrigin = '100% 0';
    dock.style.transform = `translate(${at.x - anchors.dockEnd.x}px, ${at.y - anchors.dockEnd.y}px) scale(${s})`;
    dockScaleRef.current = s;
    const maestro = project(WORLD_CENTER);
    scope.style.setProperty('--canvas-maestro-x', `${maestro.x}px`);
    scope.style.setProperty('--canvas-maestro-y', `${maestro.y}px`);
  });
  // The demo loop reads the camera and the entry lines as of the last render (`CanvasDemoFlow`).
  const demoGeoRef = useRef<DemoFlowGeo | null>(null);
  demoGeoRef.current = entryGeo
    ? {
        project: entryGeo.project,
        k: entryGeo.k,
        entryPoints: Object.fromEntries(entryGeo.lines.map((l) => [l.line.entry, l.pts])),
        agents: () => agentNodesRef.current.map((n) => orbitedRef.current(n)),
        agentR: CANVAS_AGENTS.r,
        plates: () => platesRef.current,
        plateR: CANVAS_CHANNEL.r,
      }
    : null;
  // Every world-space line the canvas draws: one per channel, and the one to the tools row when it has tools.
  const canvasLines = useMemo(
    () => [...channelLines, ...(world.nodes.some((n) => n.region === 'connectors') ? [CANVAS_CONNECTORS_LINE] : [])],
    [channelLines, world],
  );
  // ## The canvas's demo loop (`CanvasDemoFlow`): what it rides and who it pulses — this account's own map
  const demo = useMemo(() => {
    if (!bleed) return null;
    const lineOf = Object.fromEntries(channelLines.map((l) => [l.id, l]));
    const channels: DemoFlowChannel[] = world.nodes
      .filter((n) => n.region === 'channelsIn' && !n.folded && lineOf[n.id])
      .map((n) => ({ id: n.id, label: nodeLabel(n, t, maestroName), slug: n.slug ?? '', centre: { x: n.x, y: n.y }, line: lineOf[n.id] }));
    const categories: DemoFlowCategory[] = CANVAS_ENTRY_LINES.filter((l) => l.entry !== 'brief').map((l) => ({
      key: l.entry,
      label: t(HOME3_CANVAS_LABEL_KEY[l.entry as Home3Destination]),
      color: l.color,
    }));
    const tools = world.nodes.filter((n) => n.region === 'connectors' && !n.action && !n.folded);
    return {
      channels,
      categories,
      agentIds: world.nodes.filter((n) => n.region === 'agents').map((n) => n.id),
      connectorIds: tools.map((n) => n.id),
      connectorsLine: tools.length ? CANVAS_CONNECTORS_LINE : null,
      maestroId: world.nodes.find((n) => n.region === 'centre')?.id ?? '',
    };
  }, [bleed, channelLines, world, t, maestroName]);
  // A node's pulse, as the loop asks for it: its body, by scale keyframes (the hover's transform is untouched).
  const pulseNode = useCallback((id: string, scale: number[], ms: number) => {
    const body = svgRef.current?.querySelector(`[data-node="${CSS.escape(id)}"] .${styles.body}`);
    return body?.animate(
      scale.map((v) => ({ scale: String(v) })),
      { duration: ms, easing: 'ease-out' },
    );
  }, []);
  // The agents ring turns, once every two minutes, about the Maestro — as the prototype's orbit does. It stops
  // while a card is open (the card is anchored to where the node is), and never runs under reduced motion.
  const orbitRef = useRef<SVGGElement | null>(null);
  const orbitAnim = useRef<Animation | null>(null);
  useEffect(() => {
    const g = orbitRef.current;
    if (!bleed || reducedMotion || !g) return undefined;
    const a = g.animate([{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }], { duration: CANVAS_ORBIT_MS, iterations: Infinity });
    orbitAnim.current = a;
    return () => {
      a.cancel();
      orbitAnim.current = null;
    };
  }, [bleed, reducedMotion]);
  useEffect(() => {
    const a = orbitAnim.current;
    if (!a) return;
    if (selected || paused) a.pause();
    else a.play();
  }, [selected, paused]);
  /** Where the orbit has carried a point of the ring, at this moment. */
  const orbited = (p: { x: number; y: number }) => {
    const a = orbitAnim.current;
    const angle = a ? ((Number(a.currentTime ?? 0) % CANVAS_ORBIT_MS) / CANVAS_ORBIT_MS) * 2 * Math.PI : 0;
    const dx = p.x - WORLD_CENTER.x;
    const dy = p.y - WORLD_CENTER.y;
    return { x: WORLD_CENTER.x + dx * Math.cos(angle) - dy * Math.sin(angle), y: WORLD_CENTER.y + dx * Math.sin(angle) + dy * Math.cos(angle) };
  };
  // The demo loop asks for the agents every frame, between renders, so it reads them through refs.
  const orbitedRef = useRef(orbited);
  orbitedRef.current = orbited;
  const agentNodesRef = useRef<MapNode[]>([]);
  agentNodesRef.current = bleed ? world.nodes.filter((n) => n.region === 'agents') : [];
  // The plates nothing of the loop's may paint over: every channel and every tool.
  const platesRef = useRef<{ x: number; y: number }[]>([]);
  platesRef.current = bleed ? world.nodes.filter((n) => n.region === 'channelsIn' || n.region === 'connectors').map((n) => ({ x: n.x, y: n.y })) : [];
  const tween = useRef<number | null>(null);

  // Viewport size, from the host — the only measurement on this screen.
  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      // The canvas measures its dock in the same step, so the camera moves in the same render as the size.
      if (bleedRef.current && entryLayerRef.current && syncDockRef.current) {
        syncDockRef.current();
        return;
      }
      const { width, height } = entry.contentRect;
      setViewport((prev) => (prev.w === Math.round(width) && prev.h === Math.round(height) ? prev : { w: Math.round(width), h: Math.round(height) }));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const stopTween = useCallback(() => {
    if (tween.current !== null) cancelAnimationFrame(tween.current);
    tween.current = null;
  }, []);

  // ## The canvas re-measures as its page moves
  // The canvas changes under the map: the side chat docks (the pane narrows), the chat is dragged wider, the menu
  // collapses, the column's labels change with the locale. Whenever the host's size, the column's size or the
  // free middle of the canvas changes, a camera still at REST re-centres the octopus there; one the reader has
  // panned or zoomed keeps its place and zoom, taking only the host's new aspect (so a narrower host never
  // re-scales the map — and left on the old aspect, the svg's `slice` would scale the whole map past the screen
  // layer and the entry lines would miss their dots). Measured, frame by frame while anything moves.
  const dockSync = useRef<{ vpW: number; vpH: number; key: string } | null>(null);
  const syncDock = useCallback(() => {
    const layer = entryLayerRef.current;
    const host = hostRef.current;
    if (!layer || !host) return;
    const box = host.getBoundingClientRect();
    const vp = { w: Math.round(box.width), h: Math.round(box.height) };
    const sameSize = (prev: { w: number; h: number }) => (prev.w === vp.w && prev.h === vp.h ? prev : vp);
    const anchors = measureEntryAnchors(layer, host, dockScaleRef.current);
    // No dock mounted (yet): the host's size is still the map's, the camera simply has nothing to follow.
    if (!anchors) {
      setViewport(sameSize);
      return;
    }
    const free = measureFreeCentre(layer, anchors.origin);
    freeCentreRef.current = free;
    columnRef.current = anchors.dockSize;
    setEntryAnchors((prev) => (prev && sameAnchors(prev, anchors) ? prev : anchors));
    const before = dockSync.current;
    const key = `${free?.x ?? ''},${free?.y ?? ''},${anchors.dockSize.w},${anchors.dockSize.h}`;
    dockSync.current = { vpW: vp.w, vpH: vp.h, key };
    const changed = !!before && (before.key !== key || before.vpW !== vp.w || before.vpH !== vp.h);
    if (vp.w > 0) {
      setView((v) => {
        // At rest, every measure re-rests the octopus — with or without a measure before this one, which a
        // remount clears — so whatever settles last on a fresh load (the menu sliding to its width, the type
        // loading under the header) still leaves it in the middle.
        if (atRest(v)) {
          const next = restViewRef.current(vp);
          if (Math.abs(next.x - v.x) < 0.01 && Math.abs(next.y - v.y) < 0.01 && Math.abs(next.w - v.w) < 0.01 && Math.abs(next.h - v.h) < 0.01) return v;
          stopTween();
          restRef.current = next;
          return next;
        }
        if (!before || !changed) return v;
        stopTween();
        const k = before.vpW / Math.max(v.w, 1);
        return { x: v.x, y: v.y, w: vp.w / k, h: vp.h / k };
      });
    }
    setViewport(sameSize);
  }, [stopTween]);
  syncDockRef.current = bleed ? syncDock : null;
  // Off the host's own resize (above), the dock is watched for its size and for its slide: a transition on the
  // dock moves it without resizing anything, so while one runs the dock is re-measured every frame.
  useLayoutEffect(() => {
    if (!bleed || !entryLayer) return undefined;
    const scope = entryLayer.parentElement;
    if (!scope) return undefined;
    syncDock();
    const ro = new ResizeObserver(() => syncDock());
    ro.observe(entryLayer);
    const column = scope.querySelector<HTMLElement>('[data-entry-column]');
    if (column) ro.observe(column);
    // …and what the octopus is centred against (`measureFreeCentre`): a header that grows as its type loads, the
    // panel, the chat bar's slot.
    for (const name of ['header', 'panel', 'chat']) {
      const el = scope.querySelector<HTMLElement>(`[data-canvas-${name}]`);
      if (el) ro.observe(el);
    }
    const dock = column?.parentElement ?? null;
    let running = 0;
    let frame: number | null = null;
    const loop = () => {
      syncDock();
      frame = running > 0 ? requestAnimationFrame(loop) : null;
    };
    const onRun = (e: TransitionEvent) => {
      if (e.target !== dock) return;
      running += 1;
      if (frame === null) frame = requestAnimationFrame(loop);
    };
    const onEnd = (e: TransitionEvent) => {
      if (e.target !== dock) return;
      running = Math.max(0, running - 1);
      if (running === 0) syncDock();
    };
    scope.addEventListener('transitionrun', onRun);
    scope.addEventListener('transitionend', onEnd);
    scope.addEventListener('transitioncancel', onEnd);
    return () => {
      ro.disconnect();
      scope.removeEventListener('transitionrun', onRun);
      scope.removeEventListener('transitionend', onEnd);
      scope.removeEventListener('transitioncancel', onEnd);
      if (frame !== null) cancelAnimationFrame(frame);
      dockSync.current = null;
    };
  }, [bleed, entryLayer, syncDock]);

  const animateTo = useCallback(
    (target: ViewBox, ms: number) => {
      stopTween();
      if (ms <= 0 || reducedMotion) {
        setView(target);
        return;
      }
      const from = viewRef.current;
      const start = performance.now();
      const step = (now: number) => {
        const tt = Math.min((now - start) / ms, 1);
        setView(lerpView(from, target, easeInOut(tt)));
        if (tt < 1) tween.current = requestAnimationFrame(step);
        else tween.current = null;
      };
      tween.current = requestAnimationFrame(step);
    },
    [reducedMotion, stopTween],
  );

  // The entry choreography: whole world, hold, then land. Once per mount. The hold timer lives in a ref
  // and reads the viewport through a ref, because the viewport is re-measured as the rest of the screen
  // settles (tile counts landing change the map's height) — an effect that owned the timer was cleaned up
  // by that re-measure and the map never landed on the brief. The Agents destination frames the ring at once.
  const viewportRef = useRef(viewport);
  viewportRef.current = viewport;
  // ## One camera, two rule sets
  // The canvas (`bleed`) has its own default view, 30%–200% zoom range and bounds (`canvas-layout.ts`);
  // every other map keeps the shared `map-camera.ts` rules the phones mirror. Every camera move below goes
  // through these three, read through a ref so the native wheel listener never holds a stale choice.
  const bleedRef = useRef(bleed);
  bleedRef.current = bleed;
  const panView = useCallback(
    (v: ViewBox, dx: number, dy: number, vp: { w: number; h: number }) => (bleedRef.current ? canvasPan(v, dx, dy, vp) : pan(v, dx, dy, vp)),
    [],
  );
  const zoomView = useCallback(
    (v: ViewBox, factor: number, at: { x: number; y: number }) =>
      bleedRef.current ? canvasZoomAbout(v, factor, at, viewportRef.current) : zoomAbout(v, factor, at),
    [],
  );
  /** The resting frame: on the canvas, 100% with the Maestro over the chat bar's centre and the octopus centred
   *  between the header and the bar (`syncDock` measures them); else the whole world. */
  const restView = useCallback(
    (vp: { w: number; h: number }) =>
      bleedRef.current
        ? canvasDefaultView(vp, canvasGraphBounds(worldNodesRef.current, columnRef.current), freeCentreRef.current ?? { x: vp.w / 2, y: vp.h / 2 })
        : frameWorld(vp),
    [],
  );
  restViewRef.current = restView;
  // The octopus's box changes as its nodes arrive (the team, the channels, the tools): a camera at rest follows.
  useEffect(() => {
    if (!bleed || !restRef.current) return;
    setView((v) => {
      if (!atRest(v)) return v;
      restRef.current = restView(viewportRef.current);
      return restRef.current;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-centres on the world's nodes only
  }, [world.nodes]);
  const landTarget = useCallback(
    (vp: { w: number; h: number }) => (landOn === 'world' ? restView(vp) : frame(regionRect(landOn === 'brief' ? 'landing' : 'agents'), vp, 0.06)),
    [landOn, restView],
  );
  // The SMALL band's frame: the meeting and the Maestro under it — or, with no meeting on the map, the ring.
  const bandFrame = useCallback(
    (vp: { w: number; h: number }) => frame(regionRect(landOn === 'world' ? 'agents' : 'landing'), vp, 0.06),
    [landOn],
  );
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    // The canvas's resting frame is measured off its dock, so it lands once the dock is measured.
    if (entered || viewport.w === 0 || (bleed && !entryAnchors)) return;
    setEntered(true);
    if (compact || landOn === 'agents') {
      setView(landOn === 'agents' ? landTarget(viewport) : bandFrame(viewport));
      setSelected(null);
      return;
    }
    const rest = restView(viewport);
    restRef.current = bleed ? rest : null;
    setView(rest);
    // Landing on the resting frame IS the opening frame — there is nothing to hold for and tween to.
    if (landOn === 'world') return;
    holdTimer.current = setTimeout(
      () => {
        holdTimer.current = null;
        animateTo(landTarget(viewportRef.current), ENTRY_ZOOM_MS);
      },
      reducedMotion ? 0 : ENTRY_WORLD_HOLD_MS,
    );
  }, [entered, viewport, compact, landOn, animateTo, landTarget, bandFrame, restView, reducedMotion, bleed, entryAnchors]);
  // A fresh load keeps settling after the octopus first rests — the menu slides to its width, the type loads under
  // the header — and not every step of that resizes something the observers watch. So for its first moments the
  // canvas re-measures every frame, and once more when the fonts are in; a camera still at rest follows.
  useEffect(() => {
    if (!bleed || !entered) return undefined;
    let raf = 0;
    const until = performance.now() + 1500;
    const settle = () => {
      syncDockRef.current?.();
      if (performance.now() < until) raf = requestAnimationFrame(settle);
    };
    raf = requestAnimationFrame(settle);
    let live = true;
    void document.fonts?.ready.then(() => {
      if (live) syncDockRef.current?.();
    });
    return () => {
      live = false;
      cancelAnimationFrame(raf);
    };
  }, [bleed, entered]);
  useEffect(
    () => () => {
      if (holdTimer.current) clearTimeout(holdTimer.current);
      stopTween();
    },
    [stopTween],
  );

  // The small band carries no card: a selection made on the large map is dropped when a section opens.
  useEffect(() => {
    if (compact) setSelected(null);
  }, [compact]);
  // A tick on the timeline picks its event: the card opens over the node the particle landed on.
  // Each pick carries a sequence number, so the same tick opens its card again; a map that mounts after
  // a pick (the phone's overlay) does not replay it.
  const pickSeen = useRef(picked?.seq ?? 0);
  useEffect(() => {
    if (!picked || compact || picked.seq === pickSeen.current) return;
    pickSeen.current = picked.seq;
    setSelected({ kind: 'event', event: picked.event });
  }, [picked, compact]);

  // Small ⇄ large: re-frame the whole world when the host changes shape. The host's new size
  // arrives a frame or two LATER (the ResizeObserver), so the frame is redone for every measure that lands
  // inside the re-frame window — a frame computed for the old aspect is sliced at the sides by the svg.
  const shapeBefore = useRef(String(compact));
  const reframeUntil = useRef(0);
  useEffect(() => {
    const shape = String(compact);
    if (shapeBefore.current === shape) return;
    shapeBefore.current = shape;
    if (viewportRef.current.w === 0) return;
    // A shape change during the entry hold ends the hold: the map re-frames for its new box and stays there
    // rather than tweening onto the brief inside an expanded map a second later.
    if (holdTimer.current) {
      clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
    reframeUntil.current = performance.now() + ENTRY_ZOOM_MS;
    // The band shows the meeting and the Maestro (the landing), not the whole world shrunk to a strip.
    animateTo(compact ? bandFrame(viewportRef.current) : restView(viewportRef.current), ENTRY_ZOOM_MS / 2);
  }, [compact, animateTo, bandFrame, restView]);
  useEffect(() => {
    const left = reframeUntil.current - performance.now();
    if (left <= 0 || viewport.w === 0) return;
    animateTo(compact ? bandFrame(viewport) : restView(viewport), Math.max(160, left / 2));
  }, [viewport, compact, animateTo, bandFrame, restView]);

  // Pan by drag; a click (under 4px of travel) on empty ground clears the selection (and, with a section
  // open, brings the large map back).
  // "Ground" is decided at pointerDOWN: once the svg captures the pointer, every later event is retargeted
  // to the svg itself, so a pointerup's target can no longer tell a node from empty ground.
  const drag = useRef<{ x: number; y: number; moved: boolean; onGround: boolean } | null>(null);
  // Pinch: every active pointer by id; with two down, their distance scales the view about their midpoint
  // and the midpoint's travel pans it — the phone's two-finger zoom. `touch-action: none` on the svg keeps
  // the browser's own page zoom out of it.
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ dist: number; mid: { x: number; y: number } } | null>(null);
  const pinchState = (el: SVGSVGElement) => {
    const [a, b] = [...pointers.current.values()];
    const rect = el.getBoundingClientRect();
    return { dist: Math.hypot(b.x - a.x, b.y - a.y), mid: { x: (a.x + b.x) / 2 - rect.left, y: (a.y + b.y) / 2 - rect.top } };
  };
  const onPointerDown = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (e.button !== 0) return;
    stopTween();
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size >= 2) {
      // A second (or third) finger: the drag becomes a pinch, and the tap it might have been is off.
      drag.current = null;
      pinch.current = pinchState(e.currentTarget);
      e.currentTarget.setPointerCapture(e.pointerId);
      return;
    }
    const target = e.target as Element;
    const onGround = target === e.currentTarget || target.classList.contains(styles.ground);
    drag.current = { x: e.clientX, y: e.clientY, moved: false, onGround };
    // No capture yet: capturing here retargets the pointerup to the svg, and the browser then fires the
    // CLICK at the svg too (the common ancestor of down and up) — so a node's onClick never ran. The
    // pointer is captured only once a drag actually starts (see onPointerMove).
  };
  const onPointerMove = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinch.current && pointers.current.size >= 2) {
      const prev = pinch.current;
      const next = pinchState(e.currentTarget);
      pinch.current = next;
      if (prev.dist > 0 && next.dist > 0) {
        const vp = viewportRef.current;
        userMovedRef.current = true;
        setView((v) => {
          const at = toWorld(v, prev.mid, vp);
          const zoomed = zoomView(v, next.dist / prev.dist, at);
          return panView(zoomed, next.mid.x - prev.mid.x, next.mid.y - prev.mid.y, vp);
        });
      }
      return;
    }
    const d = drag.current;
    if (!d) return;
    // A pointerup that never reached the svg (a node stopped it, the pointer left the window) must not
    // leave a phantom drag behind: with no button down there is no drag, whatever the ref remembers.
    if (e.buttons === 0) {
      drag.current = null;
      return;
    }
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (!d.moved && Math.hypot(dx, dy) < 4) return;
    if (!d.moved) e.currentTarget.setPointerCapture(e.pointerId);
    d.moved = true;
    d.x = e.clientX;
    d.y = e.clientY;
    userMovedRef.current = true;
    setView((v) => panView(v, dx, dy, viewport));
  };
  const onPointerUp = (e: ReactPointerEvent<SVGSVGElement>) => {
    pointers.current.delete(e.pointerId);
    if (pinch.current) {
      // The pinch ends when the second finger lifts; the remaining finger does not become a drag or a tap.
      drag.current = null;
      if (pointers.current.size < 2) pinch.current = null;
      if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
      return;
    }
    const d = drag.current;
    drag.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    if (d && !d.moved && (d.onGround || compact)) {
      setSelected(null);
      // A press on the empty ground only closes a card, and closing logs nothing (the Activity Map's BI
      // contract). In the small state (a section open under the map, `?tab=`) the press brings the map back.
      if (compact) logPageClickBi({ page_name: ACTIVITY_MAP_BI_PAGE, button_name: 'activity-map-enlarge' });
      onGroundClick?.();
    }
  };
  // A cancelled pointer (the browser took the gesture) is not a click on the ground.
  const onPointerCancel = (e: ReactPointerEvent<SVGSVGElement>) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    drag.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };
  // Wheel: plain wheel PANS (the natural reading of a scroll gesture on a map); ctrl/⌘ + wheel — which is
  // also what a trackpad pinch arrives as — ZOOMS about the pointer. A NATIVE, non-passive listener: React's
  // onWheel is passive, so it could not preventDefault, and the page would scroll/browser-zoom underneath.
  const svgRef = useRef<SVGSVGElement | null>(null);
  const lastPanAt = useRef(0);
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      const vp = viewportRef.current;
      // A pan the camera cannot make (the map is already at that edge) is NOT claimed: the page scrolls
      // instead, so a reader wheeling down the home is not trapped on the map. The check comes before
      // preventDefault, which is what claims the gesture.
      const legacy = (e as WheelEvent & { wheelDeltaY?: number }).wheelDeltaY ?? 0;
      const notchLike = e.deltaMode !== 0 || (e.deltaX === 0 && legacy !== 0 && legacy % 120 === 0);
      const now = performance.now();
      const mouseWheel = notchLike && now - lastPanAt.current > 150;
      const zooming = e.ctrlKey || e.metaKey || mouseWheel;
      if (!zooming) {
        const next = panView(viewRef.current, -e.deltaX, -e.deltaY, vp);
        // Stuck on the axis the gesture is mostly about: a vertical scroll at the bottom edge scrolls the
        // page even if the map could still slide sideways a little.
        const vertical = Math.abs(e.deltaY) >= Math.abs(e.deltaX);
        const stuck = vertical ? Math.abs(next.y - viewRef.current.y) < 0.01 : Math.abs(next.x - viewRef.current.x) < 0.01;
        if (stuck && lastPanAt.current < now - 150) return;
      }
      e.preventDefault();
      // The frame collapses the sidebar on a dominantly-horizontal wheel (a trackpad swipe); a pan across
      // the map is exactly that gesture, so it must not travel past the map.
      e.stopPropagation();
      stopTween();
      // A MOUSE wheel zooms (the map convention on Windows and with any mouse): its deltas come in whole
      // line steps (deltaMode 1) or in 120-multiples of the legacy wheelDelta, with no horizontal part. A
      // trackpad's two-finger scroll (fractional deltas, often with deltaX) pans; its pinch arrives as
      // ctrl+wheel and zooms.
      // A trackpad streams events every ~16 ms, and one in forty of its pixel deltas happens to be a
      // 120-multiple — so a notch-looking event that follows a pan within 150 ms is still the pan. A mouse
      // notch is isolated in time.
      if (!zooming) lastPanAt.current = now;
      if (e.ctrlKey || e.metaKey || mouseWheel) {
        const rect = el.getBoundingClientRect();
        const at = toWorld(viewRef.current, { x: e.clientX - rect.left, y: e.clientY - rect.top }, vp);
        const factor = mouseWheel && !(e.ctrlKey || e.metaKey) ? (e.deltaY < 0 ? 1.25 : 1 / 1.25) : Math.pow(1.01, -e.deltaY);
        userMovedRef.current = true;
        setView((v) => zoomView(v, factor, at));
        return;
      }
      userMovedRef.current = true;
      setView((v) => panView(v, -e.deltaX, -e.deltaY, vp));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [stopTween, panView, zoomView]);
  const onDoubleClick = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const at = toWorld(viewRef.current, { x: e.clientX - rect.left, y: e.clientY - rect.top }, viewport);
    stopTween();
    userMovedRef.current = true;
    setView((v) => zoomView(v, 1.6, at));
  };
  // The zoom pill is the canvas's alone (portalled into its player band), so it reports the Activity Map's names.
  const zoomBy = (factor: number, name: 'activity-map-zoom:in' | 'activity-map-zoom:out') => {
    logPageClickBi({ page_name: ACTIVITY_MAP_BI_PAGE, button_name: name });
    stopTween();
    userMovedRef.current = true;
    setView((v) => {
      // On the canvas a press is a ten-point step of its 100% (`canvasZoomStep`); elsewhere, the factor.
      const f = bleedRef.current ? canvasZoomStep(canvasZoom(v, viewportRef.current), factor > 1 ? 1 : -1) : factor;
      return zoomView(v, f, { x: v.x + v.w / 2, y: v.y + v.h / 2 });
    });
  };
  const fit = () => {
    logPageClickBi({ page_name: ACTIVITY_MAP_BI_PAGE, button_name: 'activity-map-zoom:fit' });
    // On the canvas, fit is the default view — 100%, the Maestro back over the chat bar.
    const rest = restView(viewport);
    if (bleed) {
      restRef.current = rest;
      userMovedRef.current = false;
    }
    animateTo(rest, ENTRY_ZOOM_MS / 2);
  };
  // In the small state nothing on the map is a target of its own: any click brings the large map back.
  const selectNode = useCallback(
    (node: MapNode) => {
      if (compact) {
        onGroundClick?.();
        return;
      }
      // A district is a DOOR, not a thing with a card: clicking it opens the Space's dashboard (which logs it).
      if (node.region === 'spaces') {
        if (node.href) onOpenSpace?.(node);
        return;
      }
      // Pressing the node whose card is open closes it, and a close logs nothing. Opening one logs its Activity
      // Map name — the Maestro, an agent's dot, a tray's chip, its "+" or its "+N" (`activityMapNodeClick`).
      const closing = selected?.kind === 'node' && selected.id === node.id;
      const click = closing ? null : activityMapNodeClick(node);
      if (click) {
        logPageClickBi(
          { page_name: ACTIVITY_MAP_BI_PAGE, button_name: click.name },
          { ...(click.entity ? { entity: click.entity } : {}), ...(click.slug ? { additionalFields: { slug: click.slug } } : {}) },
        );
      }
      setSelected(closing ? null : { kind: 'node', id: node.id });
    },
    [logPageClickBi, compact, onGroundClick, onOpenSpace, selected],
  );
  const selectEdge = useCallback(
    (edge: MapEdge) => {
      if (compact) {
        onGroundClick?.();
        return;
      }
      logPageClickBi({ page_name: ACTIVITY_MAP_BI_PAGE, button_name: 'map-edge', answer: `${edge.from}>${edge.to}` });
      setSelected({ kind: 'edge', from: edge.from, to: edge.to });
    },
    [logPageClickBi, compact, onGroundClick],
  );
  const selectEvent = useCallback(
    (event: MapEvent) => {
      if (compact) {
        onGroundClick?.();
        return;
      }
      logPageClickBi({ page_name: ACTIVITY_MAP_BI_PAGE, button_name: 'map-particle', answer: event.kind });
      setSelected({ kind: 'event', event });
    },
    [logPageClickBi, compact, onGroundClick],
  );

  const particleLanded = useCallback(
    (flight: Flight) => {
      settle(flight.event.id);
      setLanded((prev) => ({ ...prev, [flight.event.to]: Date.now() }));
      const b = bucketOf(flight.event);
      if (b) onLand?.(b);
    },
    [settle, onLand],
  );

  const toScreen = (p: { x: number; y: number }) => ({ x: ((p.x - view.x) / view.w) * viewport.w, y: ((p.y - view.y) / view.h) * viewport.h });
  const anchorFor = (sel: MapSelection): { x: number; y: number } | null => {
    if (sel.kind === 'node') {
      const n = nodes[sel.id];
      return n && bleed && n.region === 'agents' ? orbited(n) : n ?? null;
    }
    if (sel.kind === 'edge') {
      const a = nodes[sel.from];
      const b = nodes[sel.to];
      return a && b ? { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } : null;
    }
    return nodes[sel.event.to] ?? null;
  };
  const card = selected ? describe(selected) : null;
  const anchor = selected ? anchorFor(selected) : null;

  const renderNode = (node: MapNode) => (
    <MapNodeView
      key={node.id}
      node={node}
      selected={selected?.kind === 'node' && selected.id === node.id}
      onSelect={selectNode}
      label={nodeLabel(node, t, maestroName)}
      openLabel={t('home.map.openSpace')}
      landedAt={landed[node.id]}
      order={orderOf(node)}
      badge={badges?.[node.id]}
      canvas={bleed}
    />
  );

  // The canvas's ground, for this canvas's width (the 100% it is sized for follows the width).
  const ground = canvasGround(viewport.w);
  const hostClass = `${styles.host}${compact ? ` ${styles.compact}` : ''}${bleed ? ` ${styles.bleed}` : ''}`;
  const scene = (
    <>
      <svg
        ref={svgRef}
        className={styles.svg}
        viewBox={toSvgViewBox(view)}
        preserveAspectRatio="xMidYMid slice"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onDoubleClick={onDoubleClick}
        role="img"
        aria-label={t('home.map.aria')}
      >
        <defs>
          {bleed ? (
            // The canvas's own ground (`canvasGround`): the frame's grid, sitting on the Maestro as it does there.
            <pattern
              id="home-map-dots"
              x={WORLD_CENTER.x + ground.dx - ground.pitchX / 2}
              y={WORLD_CENTER.y + ground.dy - ground.pitchY / 2}
              width={ground.pitchX}
              height={ground.pitchY}
              patternUnits="userSpaceOnUse"
            >
              <circle cx={ground.pitchX / 2} cy={ground.pitchY / 2} r={ground.r} className={styles.canvasGroundDot} />
            </pattern>
          ) : (
            <pattern id="home-map-dots" width="20" height="20" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="0.8" className={styles.dot} />
            </pattern>
          )}
          <clipPath id="home-map-logo-clip">
            <rect x={-NODE_R.small} y={-NODE_R.small} width={NODE_R.small * 2} height={NODE_R.small * 2} rx={9} />
          </clipPath>
        </defs>
        <rect x={-WORLD_W * 3} y={-WORLD_H * 3} width={WORLD_W * 7} height={WORLD_H * 7} fill="url(#home-map-dots)" className={styles.ground} />

        {/* Region labels — small, out of the way, hidden when the band is short. */}
        {!compact && (
          <g className={`${styles.regionLabels} ${styles.late}`} aria-hidden>
            {/* The canvas frame draws its channel plates with no title. */}
            {!bleed && (
              <text transform={`translate(30 ${WORLD_CENTER.y}) rotate(-90)`} textAnchor="middle">
                {t('home.map.channelsIn').toUpperCase()}
              </text>
            )}
            {/* The canvas has no outreach column — its entry points sit outside the map. */}
            {!bleed && (
              <text transform={`translate(${WORLD_W - 30} ${WORLD_CENTER.y}) rotate(90)`} textAnchor="middle">
                {t('home.map.outreach').toUpperCase()}
              </text>
            )}
            {/* The canvas frame draws its connectors row with no title. */}
            {!bleed && (
              <text x={WORLD_CENTER.x} y={regionRect('connectors').y + regionRect('connectors').h + 14} textAnchor="middle">
                {t('home.map.connectors').toUpperCase()}
              </text>
            )}
            {/* The canvas draws no Space districts, so it carries no label for them either. */}
            {!bleed && (
              <text x={WORLD_CENTER.x} y={SPACES_Y - 34} textAnchor="middle">
                {t('home.map.spaces').toUpperCase()}
              </text>
            )}
          </g>
        )}

        <Wiring
          world={world}
          nodes={nodes}
          reducedMotion={reducedMotion}
          onSelect={selectEdge}
          canvas={bleed}
          canvasLines={canvasLines}
        />

        {/* The entry lines' run from the ring, UNDER the agents: drawn here in the map, in the layer's px. Their
            stretch over the entry column is drawn on the top layer (`EntryLines`). */}
        {bleed && entryGeo && <EntryLinesUnder geo={entryGeo} view={view} />}

        <g className={styles.nodes}>
          {/* The six buckets are never drawn: the entry TILES right under the map are them. Particles still
              fly to a bucket's spot — off the bottom edge, toward the tile they bump. On the canvas the agents
              ride their own group, which the orbit turns. */}
          {world.nodes
            .filter((node) => node.region !== 'buckets' && !(bleed && node.region === 'agents'))
            .map(renderNode)}
          {bleed && (
            <g ref={orbitRef} className={styles.orbit} style={{ transformOrigin: `${WORLD_CENTER.x}px ${WORLD_CENTER.y}px` }}>
              {world.nodes.filter((node) => node.region === 'agents').map(renderNode)}
            </g>
          )}
        </g>

        {/* Every line's dot sits ON its items — a channel's on its plate (the user's call, 2026-11-03: it used to sit
            under it, half covered by the plate's edge), the connectors line's on the tools row — so they draw after
            the items. They never take the pointer: a press on one is a press on the plate or tool under it. */}
        {bleed && (
          <g aria-hidden className={styles.canvasLines}>
            {canvasLines.map((line) => (
              <CanvasDot key={line.id} x={line.dot.x} y={line.dot.y} color={line.dotColor} opacity={line.dotOpacity} scale={line.dotScale} />
            ))}
          </g>
        )}

        {/* The account's real activity flies on every map but the canvas, whose life is the demo loop instead
            (`CanvasDemoFlow`) — so nothing replays on load there and nothing lands with a ripple. */}
        {!bleed && (
          <Particles flights={flights} nodes={nodes} reducedMotion={reducedMotion} onLanded={particleLanded} onSelect={selectEvent} />
        )}

        {/* The meeting, as a real card on the map. HTML inside the canvas, so it pans and zooms with it. */}
        {briefCard && (
          <foreignObject
            x={WORLD_CENTER.x - BRIEF_CARD.w / 2}
            y={nodes[BRIEF_ID] ? nodes[BRIEF_ID].y - BRIEF_CARD.h / 2 : 0}
            width={BRIEF_CARD.w}
            height={BRIEF_CARD.h}
            className={styles.briefHost}
            onPointerDown={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            {briefCard}
          </foreignObject>
        )}
      </svg>

      {/* The canvas's zoom control (Figma 2045:101956): a pill at the player band's inline end — − · the zoom ·
          +, then the reset to the default view. Portalled into the band (`zoomSlot`). */}
      {bleed &&
        zoomSlot &&
        createPortal(
          <div className={styles.canvasZoom} role="group" aria-label={t('home.map.zoom')}>
            <span className={styles.canvasZoomSteps}>
              <IconButton
                size="small"
                className={styles.canvasZoomButton}
                onClick={() => zoomBy(1 / 1.5, 'activity-map-zoom:out')}
                aria-label={t('home.map.zoomOut')}
                title={t('home.map.zoomOut')}
              >
                <Minus size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
              </IconButton>
              <Typography variant="tiny-semi-bold" component="span" color="primary" className={styles.canvasZoomLevel}>
                {`${Math.round(canvasZoom(view, viewport) * 100)}%`}
              </Typography>
              <IconButton
                size="small"
                className={styles.canvasZoomButton}
                onClick={() => zoomBy(1.5, 'activity-map-zoom:in')}
                aria-label={t('home.map.zoomIn')}
                title={t('home.map.zoomIn')}
              >
                <Plus size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
              </IconButton>
            </span>
            <IconButton
              size="small"
              className={`${styles.canvasZoomButton} ${styles.canvasZoomFit}`}
              onClick={fit}
              aria-label={t('home.map.fit')}
              title={t('home.map.fit')}
            >
              <Focus size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
            </IconButton>
          </div>,
          zoomSlot,
        )}

      {/* The foot — only in the large state: the control bar (activity · + · − · fit) and, beside it, the
          timeline. The small band is a static preview with no chrome. */}
      {!compact && !bleed && (
        <div className={styles.foot}>
          <Card size="sm" disablePadding className={styles.bar} role="group" aria-label={t('home.map.zoom')}>
            {/* Every control says what it does on hover (`title`), not only to a screen reader. */}
            <IconButton size="small" variant="outlined" aria-label={t('home.map.timeline')} title={t('home.map.timeline')} onClick={onTimeline}>
              <Activity size={16} aria-hidden />
            </IconButton>
            <span className={styles.barGap} />
            <IconButton
              size="small"
              variant="outlined"
              onClick={() => zoomBy(1.5, 'activity-map-zoom:in')}
              aria-label={t('home.map.zoomIn')}
              title={t('home.map.zoomIn')}
            >
              <Plus size={16} aria-hidden />
            </IconButton>
            <IconButton
              size="small"
              variant="outlined"
              onClick={() => zoomBy(1 / 1.5, 'activity-map-zoom:out')}
              aria-label={t('home.map.zoomOut')}
              title={t('home.map.zoomOut')}
            >
              <Minus size={16} aria-hidden />
            </IconButton>
            <IconButton size="small" variant="outlined" onClick={fit} aria-label={t('home.map.fit')} title={t('home.map.fit')}>
              <Scan size={16} aria-hidden />
            </IconButton>
          </Card>
          {timeline && (
            <Card size="sm" disablePadding className={styles.footTimeline}>
              {timeline}
            </Card>
          )}
        </div>
      )}

      {/* No minimap on a narrow pane or a short band: it would cover the connectors row. None on the canvas
          either — it would sit under the header and the side panel that float over it. */}
      {!compact && !bleed && viewport.w >= MINIMAP_MIN_W && (
        <Minimap
          world={world}
          view={view}
          onMove={(p) => {
            stopTween();
            setView((v) => clamp({ ...v, x: p.x - v.w / 2, y: p.y - v.h / 2 }));
          }}
          onBi={() => logPageClickBi({ page_name: ACTIVITY_MAP_BI_PAGE, button_name: 'map-minimap' })}
        />
      )}

      {bleed && demoLayer && entryLayer && demo && !reducedMotion && (
        <CanvasDemoFlow
          layer={demoLayer}
          geoRef={demoGeoRef}
          channels={demo.channels}
          categories={demo.categories}
          agentIds={demo.agentIds}
          connectorIds={demo.connectorIds}
          connectorsLine={demo.connectorsLine}
          maestroId={demo.maestroId}
          pulse={pulseNode}
          glyphFor={canvasChannelGlyph}
          paused={paused}
          fill={demoFill}
          onFill={onDemoFill}
        />
      )}

      {card &&
        anchor &&
        viewport.w > 0 &&
        (() => {
          const info = <MapInfoCard key={card.key} content={card} anchor={toScreen(anchor)} viewport={viewport} onClose={() => setSelected(null)} />;
          // On the canvas the card rides the host's top layer: over the entry lines, the dock, the header and the
          // chat bar.
          const cardLayer = popoverLayer ?? entryLayer;
          return bleed && cardLayer ? (
            createPortal(
              <CanvasCardLayer layer={cardLayer} host={hostRef.current} viewport={viewport}>
                {info}
              </CanvasCardLayer>,
              cardLayer,
            )
          ) : (
            info
          );
        })()}

      {bleed &&
        entryLayer &&
        entryGeo &&
        createPortal(<EntryLines geo={entryGeo} />, entryLayer)}
    </>
  );

  return bleed ? (
    <div ref={hostRef} className={hostClass} data-testid="home-map">
      {scene}
    </div>
  ) : (
    <Card ref={hostRef as never} variant="stone" disablePadding className={hostClass} data-testid="home-map">
      {scene}
    </Card>
  );
}

/** The standing wires — genome style — and the signal riding each connected one. */
const Wiring = memo(function Wiring({
  world,
  nodes,
  reducedMotion,
  onSelect,
  canvas = false,
  canvasLines,
}: {
  world: MapWorld;
  nodes: Record<string, MapNode>;
  reducedMotion: boolean;
  onSelect: (e: MapEdge) => void;
  /** The Activity Map canvas draws the ring in its frame's darker, longer dash, and its own lines. */
  canvas?: boolean;
  canvasLines?: CanvasLine[];
}) {
  if (canvas) {
    // ## The canvas draws the FRAME's lines (Figma 2038:96143), not the spokes
    // One line per channel (its dot → the ring) and one under the ring to the tools row, each the frame's
    // own curve and colour; their dots draw over the items, after them (see the nodes). The spokes into the Maestro, and the signals riding them, are not
    // in the frame. The entry-point lines are drawn on the screen layer (`EntryLines`). Visual only: a press
    // goes to the node, as it always did.
    return (
      <g className={styles.edges}>
        <circle cx={WORLD_CENTER.x} cy={WORLD_CENTER.y} r={AGENT_RING_R} className={`${styles.ring} ${styles.ringCanvas}`} aria-hidden />
        <g aria-hidden className={styles.canvasLines}>
          {(canvasLines ?? []).map((line) => (
            <path key={line.id} d={line.d} className={styles.canvasLine} style={{ stroke: line.color, strokeOpacity: line.opacity, strokeLinecap: line.cap }} />
          ))}
        </g>
      </g>
    );
  }
  return (
    <g className={styles.edges}>
      <circle
        cx={WORLD_CENTER.x}
        cy={WORLD_CENTER.y}
        r={AGENT_RING_R}
        className={styles.ring}
        aria-hidden
      />
      {world.edges.map((e, i) => {
        const a = nodes[e.from];
        const b = nodes[e.to];
        if (!a || !b) return null;
        const d = edgePath(a, b);
        const into = e.to === MAESTRO_ID;
        const connected = e.style === 'solid';
        const far = nodes[into ? e.from : e.to];
        const stagger = far ? orderOf(far) : i;
        return (
          <g
            key={`${e.from}>${e.to}`}
            className={styles.wire}
            data-connected={connected}
            style={{ '--d': `${FLY_BASE_MS + stagger * FLY_STAGGER_MS}ms` } as CSSProperties}
          >
            {/* A connected spoke DRAWS itself from the far end (pathLength 1 → a unit dash that slides off); a
                dotted one keeps its dash pattern and fades in instead. */}
            <path
              d={d}
              pathLength={connected ? 1 : undefined}
              className={connected ? styles.spokeOn : styles.spokeOff}
              strokeDasharray={connected ? undefined : '2 3'}
            />
            {/* A wide, invisible twin so the wire is clickable without being fat. */}
            <path
              d={d}
              className={styles.spokeHit}
              onClick={(ev) => {
                ev.stopPropagation();
                onSelect(e);
              }}
              role="button"
              tabIndex={-1}
              aria-label={`${a.label} → ${b.label}`}
            />
            {connected && !reducedMotion && (
              <circle
                r={3}
                className={`${styles.pulse} ${into ? styles.pulseInto : styles.pulseOut}`}
                style={{ offsetPath: `path("${d}")`, animationDelay: `${FLY_BASE_MS + FLY_MS + stagger * PULSE_STAGGER_MS}ms` } as CSSProperties}
              />
            )}
          </g>
        );
      })}
    </g>
  );
});

const Particles = memo(function Particles({
  flights,
  nodes,
  reducedMotion,
  onLanded,
  onSelect,
}: {
  flights: Flight[];
  nodes: Record<string, MapNode>;
  reducedMotion: boolean;
  onLanded: (flight: Flight) => void;
  onSelect: (event: MapEvent) => void;
}) {
  return (
    <g className={styles.particles}>
      {flights.map((flight, i) => {
        const a = nodes[flight.event.from];
        const b = nodes[flight.event.to];
        if (!a || !b) return null;
        // Parked particles that share a destination fan out sideways, so each stays visible and clickable.
        const siblings = flight.still ? flights.filter((f) => f.still && f.event.to === flight.event.to) : [];
        const rank = flight.still ? siblings.findIndex((f) => f.event.id === flight.event.id) : 0;
        const fan = flight.still ? (rank - (siblings.length - 1) / 2) * 16 : 0;
        return (
          <Particle
            key={`${flight.event.id}@${flight.launchAt}`}
            flight={flight}
            from={a}
            to={b}
            fan={fan}
            reducedMotion={reducedMotion}
            onLanded={onLanded}
            onSelect={onSelect}
            index={i}
          />
        );
      })}
    </g>
  );
});

/** One particle. Its `--delay` is fixed ONCE, when it mounts: a CSS animation's start is pinned at creation,
 *  so re-deriving the delay from a later `performance.now()` on every render would pull the flight EARLIER
 *  each camera frame and compress the reel during the entry tween. A `still` particle (the scrubber's) is
 *  parked over its destination and stays until the scrub moves on. */
const Particle = memo(function Particle({
  flight,
  from,
  to,
  fan,
  reducedMotion,
  onLanded,
  onSelect,
}: {
  flight: Flight;
  from: MapNode;
  to: MapNode;
  fan: number;
  index: number;
  reducedMotion: boolean;
  onLanded: (flight: Flight) => void;
  onSelect: (event: MapEvent) => void;
}) {
  const [delay] = useState(() => Math.max(0, flight.launchAt - performance.now()));
  const color = flight.event.agentId ? colorForAgent(flight.event.agentId) : 'var(--ds-text-primary)';
  const Icon = iconForKind(flight.event.kind);
  // A parked particle sits over its destination — unless that is a bucket, which is off the map's bottom
  // edge (the tiles are the buckets): then it parks under the node it left, where the reader can click it.
  // Under the origin's LABEL, not over it: an agent's name sits 28 units under its centre.
  const park =
    to.region === 'buckets'
      ? { x: from.x, y: from.y + (from.region === 'centre' ? NODE_R.maestro : NODE_R.agent) + LABEL_GAP + 16 }
      : to.region === 'spaces'
      ? { x: to.x, y: to.y - DISTRICT.h / 2 - 16 }
      : { x: to.x, y: to.y - NODE_R.small - 10 };
  // The flight IS the content of this map, so it plays under reduced motion too (the glow and the
  // heartbeat are what that setting removes). A browser without CSS motion paths on SVG (`offset-path`)
  // flies the dot along the straight line instead of the wire's curve, never parks it unflown.
  const style: CSSProperties & Record<string, string> = flight.still
    ? { transform: `translate(${park.x + fan}px, ${park.y}px)` }
    : MOTION_PATHS
    ? { offsetPath: `path("${edgePath(from, to)}")`, offsetRotate: '0deg', '--delay': `${delay}ms`, '--flight': `${flight.flightMs}ms` }
    : {
        '--x0': `${from.x}px`,
        '--y0': `${from.y}px`,
        '--x1': `${to.x}px`,
        '--y1': `${to.y}px`,
        '--delay': `${delay}ms`,
        '--flight': `${flight.flightMs}ms`,
      };
  return (
    <g
      className={flight.still ? styles.particleParked : MOTION_PATHS ? styles.particle : styles.particleLine}
      style={style}
      onAnimationEnd={() => !flight.still && onLanded(flight)}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(flight.event);
      }}
      role="button"
      tabIndex={-1}
      aria-label={flight.event.text}
    >
      <circle r={18} fill={color} className={styles.particleGlow} />
      <circle r={12} fill={color} className={styles.particleDot} />
      {Icon && (
        <svg x={-7} y={-7} width={14} height={14} viewBox="0 0 24 24" className={styles.particleGlyph}>
          <Icon size={24} aria-hidden />
        </svg>
      )}
    </g>
  );
});

/** A line dot (Figma's 12px: a 5.5 fill under a 1px page-grey ring), at `scale` px per unit of its space. */
function CanvasDot({ x, y, color, opacity, scale }: { x: number; y: number; color: string; opacity: number; scale: number }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <circle r={CANVAS_DOT.ring * scale} className={styles.canvasDotRing} />
      <circle r={CANVAS_DOT.fill * scale} style={{ fill: color, fillOpacity: opacity }} />
    </g>
  );
}

/**
 * The canvas's info card, on the screen layer over everything else: a box laid exactly over the map host (its
 * offset on the layer measured, as the entry lines measure it), so the card keeps its host coordinates and
 * opens where it always did. Only the card takes the pointer; around it, the map still drags.
 */
function CanvasCardLayer({
  layer,
  host,
  viewport,
  children,
}: {
  layer: HTMLElement;
  host: HTMLElement | null;
  viewport: { w: number; h: number };
  children: React.ReactNode;
}) {
  const [origin, setOrigin] = useState<{ x: number; y: number } | null>(null);
  useLayoutEffect(() => {
    const measure = () => {
      const base = layer.getBoundingClientRect();
      const box = (host ?? layer).getBoundingClientRect();
      setOrigin({ x: box.left - base.left, y: box.top - base.top });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(layer);
    return () => ro.disconnect();
  }, [layer, host]);
  if (!origin) return null;
  return (
    <div className={styles.cardLayer} style={{ left: origin.x, top: origin.y, width: viewport.w, height: viewport.h }}>
      {children}
    </div>
  );
}

/**
 * The entry column, as its host lays it out: the map host's origin on the entry layer, the dock's top END corner
 * there (its layout box — what the camera moves it from), and each entry point's art centre down the column, in
 * the column's own unscaled px (the frame's).
 */
type EntryAnchors = {
  origin: { x: number; y: number };
  dockEnd: { x: number; y: number };
  /** The column's laid-out size, in its own (the frame's) px. */
  dockSize: { w: number; h: number };
  artY: Record<string, number>;
};
type EntryGeometry = {
  anchors: EntryAnchors;
  /** Screen px per world unit. */
  k: number;
  /** Screen px per frame px — the scale the column is drawn at. */
  s: number;
  /** Where the dock's top end corner is on the entry layer, at this camera. */
  dock: { x: number; y: number };
  /** The entry lines' dots' x on the entry layer, where their top half starts. */
  dotX: number;
  /** A world point, on the entry layer in px. */
  project: (p: { x: number; y: number }) => { x: number; y: number };
  lines: {
    line: (typeof CANVAS_ENTRY_LINES)[number];
    p0: { x: number; y: number };
    d: string;
    /** The same line as its cubic's four points, for the demo loop's tags to ride. */
    pts: { x: number; y: number }[];
    dot: { x: number; y: number };
  }[];
};

/**
 * The entry column as laid out (`EntryAnchors`). Measured, never assumed (`syncDock` re-measures whenever the
 * host or the dock moves). The dock's corner is its LAYOUT box (offsets ignore the transform the camera puts on
 * it); the art centres are read through that transform, at `scale`, back into the column's own px. `null`
 * while the column is not mounted.
 */
function measureEntryAnchors(layer: HTMLElement, host: HTMLElement, scale: number): EntryAnchors | null {
  const scope = layer.parentElement;
  const column = scope?.querySelector<HTMLElement>('[data-entry-column]');
  const dock = column?.parentElement;
  if (!scope || !column || !dock) return null;
  const base = layer.getBoundingClientRect();
  const hostBox = host.getBoundingClientRect();
  const top = dock.getBoundingClientRect().top;
  const artY: Record<string, number> = {};
  scope.querySelectorAll<HTMLElement>('[data-entry-point]').forEach((el) => {
    const r = el.getBoundingClientRect();
    artY[el.dataset.entryPoint ?? ''] = Math.round(((r.top + r.height / 2 - top) / scale) * 100) / 100;
  });
  return {
    origin: { x: hostBox.left - base.left, y: hostBox.top - base.top },
    dockEnd: { x: dock.offsetLeft + dock.offsetWidth, y: dock.offsetTop },
    dockSize: { w: dock.offsetWidth, h: dock.offsetHeight },
    artY,
  };
}

/**
 * Where the octopus rests, in host px: across, the chat bar's centre (the Maestro sits right over it, as the
 * frame draws it), else the middle of the canvas right of the activity panel; down, the middle between the
 * header's foot and the chat bar's top. Read off the page's own elements (`data-canvas-panel` / `-header` /
 * `-chat`); an edge with no element is the canvas's own. `null` while the layer has no size.
 */
function measureFreeCentre(layer: HTMLElement, origin: { x: number; y: number }): { x: number; y: number } | null {
  const scope = layer.parentElement;
  const base = layer.getBoundingClientRect();
  if (!scope || base.width === 0) return null;
  const rect = (name: string) => scope.querySelector<HTMLElement>(`[data-canvas-${name}]`)?.getBoundingClientRect() ?? null;
  const panel = rect('panel');
  const header = rect('header');
  const chat = rect('chat');
  const x0 = panel ? panel.right - base.left : 0;
  const y0 = header ? header.bottom - base.top : 0;
  const y1 = chat ? chat.top - base.top : base.height;
  const x = chat && chat.width > 0 ? (chat.left + chat.right) / 2 - base.left : (x0 + base.width) / 2;
  return { x: Math.round(x - origin.x), y: Math.round((y0 + Math.max(y0, y1)) / 2 - origin.y) };
}

function sameAnchors(a: EntryAnchors, b: EntryAnchors): boolean {
  if (a.origin.x !== b.origin.x || a.origin.y !== b.origin.y || a.dockEnd.x !== b.dockEnd.x || a.dockEnd.y !== b.dockEnd.y) return false;
  if (a.dockSize.w !== b.dockSize.w || a.dockSize.h !== b.dockSize.h) return false;
  const keys = Object.keys(a.artY);
  return keys.length === Object.keys(b.artY).length && keys.every((key) => a.artY[key] === b.artY[key]);
}

/**
 * ## The entry-point lines, in the entry layer's SCREEN px
 *
 * Both of a line's ends are in the WORLD: its ring end, and its entry end on the entry column, which moves
 * with the graph (`CANVAS_DOCK_ANCHOR`). So the line is the frame's own at every zoom — the column's art
 * centres are measured once in its own px and carried through the camera with it. It is still laid out in
 * screen px, projected every frame, because its top half is drawn over the column on the screen layer and
 * what rides it (`CanvasDemoFlow`) reads it there.
 */
function entryLineGeometry(anchors: EntryAnchors, view: ViewBox, viewport: { w: number; h: number }): EntryGeometry {
  const k = viewport.w / Math.max(view.w, 1);
  const s = k * CANVAS_FIGMA_SCALE;
  const project = (p: { x: number; y: number }) => ({ x: anchors.origin.x + (p.x - view.x) * k, y: anchors.origin.y + (p.y - view.y) * k });
  const dock = project({ x: WORLD_CENTER.x + CANVAS_DOCK_ANCHOR.dx, y: WORLD_CENTER.y + CANVAS_DOCK_ANCHOR.dy });
  const dotX = dock.x - CANVAS_ENTRY_DOT_FROM_END * s;
  const lines = CANVAS_ENTRY_LINES.flatMap((line) => {
    const y = anchors.artY[line.entry];
    if (y === undefined) return [];
    const p0 = project(line.start);
    const p3 = { x: dotX + line.endDx * s, y: dock.y + (y + line.endDy) * s };
    const pts = canvasEntryPoints(line, p0, p3);
    return [{ line, p0, d: canvasEntryPath(line, p0, p3), pts, dot: { x: dotX, y: p3.y } }];
  });
  return { anchors, k, s, dock, dotX, project, lines };
}

/**
 * The entry lines' run from the ring, drawn IN the map so it passes under the agents and the Maestro like
 * every other line. A group maps the entry layer's px back onto the world (the inverse of `project`), so it
 * is the very same path as the top half, which draws the end of it again over the dock.
 */
function EntryLinesUnder({ geo, view }: { geo: EntryGeometry; view: ViewBox }) {
  const { anchors, k, lines } = geo;
  return (
    <g
      aria-hidden
      className={styles.canvasLines}
      transform={`translate(${view.x} ${view.y}) scale(${1 / k}) translate(${-anchors.origin.x} ${-anchors.origin.y})`}
    >
      {lines.map(({ line, d }) => (
        <path key={line.entry} d={d} className={styles.canvasLine} style={{ stroke: line.color, strokeOpacity: line.opacity, strokeLinecap: 'round' }} />
      ))}
    </g>
  );
}

/**
 * ## The entry lines' top half on the SCREEN layer over the dock
 *
 * Only the line's last stretch is drawn here (clipped at its dot), so it lands on its entry point above the
 * dock; the run from the ring is in the map, under the agents (`EntryLinesUnder`). The end dots sit on top.
 * What rides the lines is the demo loop's (`CanvasDemoFlow`), on the same layer.
 */
function EntryLines({ geo }: { geo: EntryGeometry }) {
  const clipId = useId();
  const { dotX, s, lines } = geo;

  return (
    <svg className={styles.entryOverlay} width="100%" height="100%" aria-hidden>
      <defs>
        <clipPath id={clipId}>
          <rect x={dotX} y={0} width="100%" height="100%" />
        </clipPath>
      </defs>
      {lines.map(({ line, d, dot }) => (
        <g key={line.entry}>
          <g clipPath={`url(#${CSS.escape(clipId)})`}>
            <path d={d} className={styles.canvasLine} style={{ stroke: line.color, strokeOpacity: line.opacity, strokeLinecap: 'round' }} />
          </g>
          <CanvasDot x={dot.x} y={dot.y} color={line.dotColor} opacity={line.dotOpacity} scale={s} />
        </g>
      ))}
    </svg>
  );
}

/** Whether this browser animates `offset-distance` on SVG elements (the wire-following flight). */
const MOTION_PATHS =
  typeof CSS !== 'undefined' &&
  typeof CSS.supports === 'function' &&
  CSS.supports('offset-path', 'path("M0 0 L1 1")') &&
  CSS.supports('offset-distance', '50%');

const MapNodeView = memo(function MapNodeView({
  node,
  selected,
  onSelect,
  label,
  landedAt,
  order,
  badge,
  openLabel,
  canvas = false,
}: {
  node: MapNode;
  selected: boolean;
  onSelect: (n: MapNode) => void;
  label: string;
  landedAt?: number;
  order: number;
  badge?: number;
  openLabel?: string;
  /** Drawn on the Activity Map canvas, whose frame gives some regions a look of their own. */
  canvas?: boolean;
}) {
  const off = node.connected === false;
  const common = {
    className: `${styles.node}${selected ? ` ${styles.nodeSelected}` : ''}${off ? ` ${styles.nodeOff}` : ''}`,
    transform: `translate(${node.x} ${node.y})`,
    // What the canvas's demo loop pulses a node by.
    'data-node': node.id,
    // The fly-in: from the hub's direction (a fraction of the way), staggered by the node's order on its band.
    style: {
      '--fx': `${(WORLD_CENTER.x - node.x) * 0.55}px`,
      '--fy': `${(WORLD_CENTER.y - node.y) * 0.55}px`,
      '--d': `${FLY_BASE_MS + order * FLY_STAGGER_MS}ms`,
    } as CSSProperties,
    onClick: (e: { stopPropagation: () => void }) => {
      e.stopPropagation();
      onSelect(node);
    },
    role: 'button' as const,
    tabIndex: 0,
    onKeyDown: (e: { key: string; preventDefault: () => void }) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect(node);
      }
    },
  };
  // The ripple a landing particle leaves: re-keyed per landing so it replays; gone once it has run.
  const ripple = landedAt ? <circle key={landedAt} r={NODE_R.small} className={styles.ripple} /> : null;
  switch (node.region) {
    case 'centre':
      if (canvas) {
        // The canvas frame's own Maestro tile (`CANVAS_MAESTRO`), with the shared halo — and no heartbeat ring:
        // the canvas's life is its demo loop, which pulses the tile itself.
        const m = CANVAS_MAESTRO;
        const half = m.size / 2;
        return (
          <g {...common} aria-label={label}>
            <g className={styles.fly}>
              {ripple}
              <g className={styles.body}>
                <circle r={half + 6} className={styles.halo} />
                <g transform={`translate(${-half} ${-half})`}>
                  <rect width={m.size} height={m.size} rx={m.radius} className={maestroMarkStyles.tile} />
                  {m.squares.map((p) => (
                    <rect key={`s${p.x}`} x={p.x} y={p.y} width={m.shape} height={m.shape} rx={m.squareRadius} className={maestroMarkStyles.shape} />
                  ))}
                  {m.circles.map((p) => (
                    <circle key={`c${p.x}`} cx={p.x + m.shape / 2} cy={p.y + m.shape / 2} r={m.shape / 2} className={maestroMarkStyles.shape} />
                  ))}
                </g>
              </g>
            </g>
          </g>
        );
      }
      return (
        <g {...common} aria-label={label}>
          <g className={styles.fly}>
            <circle r={NODE_R.maestro + 4} className={styles.heartbeat} />
            {ripple}
            <g className={styles.body}>
              <circle r={NODE_R.maestro + 6} className={styles.halo} />
              <svg x={-NODE_R.maestro} y={-NODE_R.maestro} width={NODE_R.maestro * 2} height={NODE_R.maestro * 2} viewBox="0 0 52 52">
                <MaestroMark size={52} />
              </svg>
            </g>
          </g>
        </g>
      );
    case 'agents': {
      const id = node.id.slice('agent:'.length);
      // The agent's own mark — the concentric dot Your Agents and the brief use (`AgentDot`), drawn as
      // native SVG ellipses at the node's size; the same shapes on iOS and Android.
      if (canvas) {
        // The canvas frame's agent: the same dot at 43.86px, on no plate and with no name under it — the
        // name stays the node's accessible label and its card's title.
        const kc = CANVAS_AGENTS.r / 32;
        return (
          <g {...common} aria-label={label}>
            <g className={styles.fly}>
              {ripple}
              <g className={styles.body}>
                <circle r={CANVAS_AGENTS.r + 6} className={styles.halo} />
                {agentDotShapes(id).map((sh, i) => (
                  <ellipse key={i} cx={(sh.cx - 32) * kc} cy={(sh.cy - 32) * kc} rx={sh.rx * kc} ry={sh.ry * kc} fill={sh.fill} />
                ))}
              </g>
            </g>
          </g>
        );
      }
      const k = NODE_R.agent / 32;
      return (
        <g {...common} aria-label={label}>
          <g className={styles.fly}>
            {ripple}
            <g className={styles.body}>
              <circle r={NODE_R.agent + 6} className={styles.halo} />
              <circle r={NODE_R.agent + 2} className={styles.agentPlate} />
              {agentDotShapes(id).map((sh, i) => (
                <ellipse key={i} cx={(sh.cx - 32) * k} cy={(sh.cy - 32) * k} rx={sh.rx * k} ry={sh.ry * k} fill={sh.fill} />
              ))}
            </g>
            <text y={NODE_R.agent + LABEL_GAP} textAnchor="middle" className={styles.nodeLabel}>
              {node.label}
            </text>
          </g>
        </g>
      );
    }
    case 'brief':
      // The meeting is drawn as a card in a foreignObject (see HomeMap); the node only anchors its wire.
      return null;
    case 'buckets': {
      const Icon = BUCKET_ICONS[node.label as BucketId] ?? File;
      return (
        <g {...common} aria-label={label}>
          <g className={styles.fly}>
            {ripple}
            <g className={styles.body}>
              <circle r={NODE_R.bucket + 6} className={styles.halo} />
              <circle r={NODE_R.bucket} className={styles.bucket} data-bucket={node.label} />
              <svg x={-8} y={-8} width={16} height={16} viewBox="0 0 24 24" className={styles.bucketGlyph}>
                <Icon size={24} aria-hidden />
              </svg>
            </g>
            <text y={NODE_R.bucket + LABEL_GAP} textAnchor="middle" className={styles.nodeLabel}>
              {label}
            </text>
          </g>
        </g>
      );
    }
    case 'spaces': {
      // A DISTRICT: a wide plate carrying the Space's icon and name — the ground its work lands on. Clicking
      // it opens the Space.
      const w = DISTRICT.w;
      const h = DISTRICT.h;
      return (
        <g {...common} aria-label={label} className={`${common.className as string} ${styles.district}`}>
          <g className={styles.fly}>
            {ripple}
            <g className={styles.body}>
              <rect x={-w / 2 - 6} y={-h / 2 - 6} width={w + 12} height={h + 12} rx={16} className={styles.halo} />
              <rect x={-w / 2} y={-h / 2} width={w} height={h} rx={12} className={styles.districtPlate} />
              {node.icon && /^https?:\/\//.test(node.icon) ? (
                <image href={node.icon} x={-w / 2 + 8} y={-10} width={20} height={20} preserveAspectRatio="xMidYMid meet" />
              ) : node.icon && node.icon.length <= 4 ? (
                <text x={-w / 2 + 18} y={6} className={styles.districtIcon}>
                  {node.icon}
                </text>
              ) : (
                <svg x={-w / 2 + 12} y={-8} width={16} height={16} viewBox="0 0 24 24" className={styles.districtGlyph}>
                  <LayoutGrid size={24} aria-hidden />
                </svg>
              )}
              {/* The name, and under it what a click does — the arrow alone did not say. */}
              <text x={-w / 2 + 38} y={0} textAnchor="start" className={styles.districtLabel}>
                {label}
              </text>
              <text x={-w / 2 + 38} y={13} textAnchor="start" className={styles.districtHint}>
                {openLabel}
              </text>
              <svg x={w / 2 - 24} y={-7} width={14} height={14} viewBox="0 0 24 24" className={styles.districtArrow}>
                <ArrowUpRight size={24} aria-hidden />
              </svg>
            </g>
          </g>
        </g>
      );
    }
    case 'connectors':
      // The canvas draws its tools as its frame does: one row of round grey plates, no names.
      if (canvas) return <ConnectorPlate node={node} common={common} label={label} ripple={ripple} />;
      if (node.action === 'connect') {
        return (
          <g {...common} aria-label={label}>
            <g className={styles.fly}>
              <g className={styles.body}>
                <rect
                  x={-NODE_R.small - 6}
                  y={-NODE_R.small - 6}
                  width={(NODE_R.small + 6) * 2}
                  height={(NODE_R.small + 6) * 2}
                  rx={12}
                  className={styles.halo}
                />
                <rect
                  x={-NODE_R.small}
                  y={-NODE_R.small}
                  width={NODE_R.small * 2}
                  height={NODE_R.small * 2}
                  rx={9}
                  className={styles.tileAction}
                  strokeDasharray="3 3"
                />
                <svg x={-8} y={-8} width={16} height={16} viewBox="0 0 24 24" className={styles.tileActionGlyph}>
                  <Plus size={24} aria-hidden />
                </svg>
              </g>
              <text y={NODE_R.small + LABEL_GAP} textAnchor="middle" className={styles.nodeLabel}>
                {label}
              </text>
            </g>
          </g>
        );
      }
      // The connector's OWN logo when the connectors screen has one (that is the icon it shows), else the
      // logo service by slug; an MCP server has neither and shows its letter.
      return (
        <TileNode
          node={node}
          common={common}
          label={label}
          logo={node.slug?.startsWith('mcp:') ? null : node.logoUrl ?? connectorLogoUrl(node.slug ?? '')}
          ripple={ripple}
        />
      );
    case 'outreach': {
      // A branded channel wears its logo; email, SMS and phone keep a glyph.
      const brand = outreachLogoUrl(node.slug ?? '');
      return (
        <TileNode
          node={node}
          common={common}
          label={label}
          logo={brand}
          glyph={brand ? undefined : <OutreachChannelGlyph slug={node.slug ?? ''} />}
          ripple={ripple}
          badge={badge}
        />
      );
    }
    default: {
      // The canvas draws the channels in as its frame does: round grey plates, a glyph, no name.
      if (canvas && node.region === 'channelsIn') return <ChannelPlate node={node} common={common} label={label} ripple={ripple} />;
      // Symphony's own surfaces and the AI-coder clients have no vendor logo — a glyph each.
      const glyph = channelGlyph(node.slug ?? '');
      if (glyph) return <TileNode node={node} common={common} label={label} logo={null} glyph={glyph} ripple={ripple} />;
      return <TileNode node={node} common={common} label={label} logo={node.logoUrl ?? connectorLogoUrl(node.slug ?? '')} ripple={ripple} />;
    }
  }
});

/** The canvas frame's glyph for each channel in (Figma 2038:96143) — lucide where the frame used it, the
 *  frame's own WhatsApp mark, and the AI coder's `Bot` (the frame draws no coder). */
const CANVAS_CHANNEL_GLYPHS: Record<string, typeof Mail> = {
  'phone-app': Smartphone,
  web: AppWindow,
  'email-in': Mail,
  'sms-in': MessageSquareText,
};

/** One turn of the canvas's agents ring (the prototype's orbit). */
const CANVAS_ORBIT_MS = 120_000;

/** A channel's glyph at 12px — the demo loop's bare marks — drawn as its plate draws it. */
function canvasChannelGlyph(slug: string): React.ReactNode {
  if (slug === 'whatsapp') return <WhatsAppCanvasGlyph width={12} height={(12 * 17) / 18} />;
  const Icon = CANVAS_CHANNEL_GLYPHS[slug] ?? (slug.startsWith('coder:') ? Bot : null);
  return Icon ? <Icon size={12} strokeWidth={CANVAS_CHANNEL.strokeWidthAt(CANVAS_CHANNEL.glyph)} aria-hidden /> : null;
}

/**
 * A channel in, as the canvas draws it (Figma 2038:96143): a 50px round plate in the light grey, the
 * glyph in ink, and NO name beside it — the frame draws none. The name is still the node's accessible label
 * and the title of the card it opens. Every size is the frame's at 100%, in world units, so it scales with
 * the camera like the rest of the map.
 */
function ChannelPlate({ node, common, label, ripple }: { node: MapNode; common: Record<string, unknown>; label: string; ripple: React.ReactNode }) {
  const slug = node.slug ?? '';
  const { r } = CANVAS_CHANNEL;
  const g = CANVAS_CHANNEL.glyphBySlug[slug] ?? CANVAS_CHANNEL.glyph;
  const strokeWidth = CANVAS_CHANNEL.strokeWidthAt(g);
  const Icon = CANVAS_CHANNEL_GLYPHS[slug] ?? (slug.startsWith('coder:') ? Bot : null);
  // The frame's WhatsApp mark, at its width; the drawing keeps its own 18:17.
  const wa = { w: CANVAS_CHANNEL.whatsapp, h: (CANVAS_CHANNEL.whatsapp * 17) / 18 };
  return (
    <g {...common} aria-label={label}>
      <g className={styles.fly}>
        {ripple}
        <g className={styles.body}>
          <circle r={r + 6} className={styles.halo} />
          <circle r={r} className={styles.channelPlate} />
          {node.folded ? (
            <text y={5} textAnchor="middle" className={styles.tileText}>
              {node.label}
            </text>
          ) : slug === 'whatsapp' ? (
            <g transform={`translate(${-wa.w / 2} ${-wa.h / 2})`} className={styles.channelGlyph}>
              <WhatsAppCanvasGlyph width={wa.w} height={wa.h} />
            </g>
          ) : Icon ? (
            <svg x={-g / 2} y={-g / 2} width={g} height={g} viewBox="0 0 24 24" className={styles.channelGlyph}>
              <Icon size={24} strokeWidth={strokeWidth} aria-hidden />
            </svg>
          ) : (
            <text y={5} textAnchor="middle" className={styles.tileText}>
              {label.charAt(0).toUpperCase()}
            </text>
          )}
        </g>
      </g>
    </g>
  );
}

/**
 * A connected tool on the canvas (Figma 2038:96143): the same 50px light-grey round plate as a channel,
 * carrying the tool's logo in a 26px box (the Wix wordmark at the frame's own size), and NO name under it —
 * the frame draws none; the name stays the node's accessible label and its card's title. The row's last
 * plate is "+ connect", a black Plus on the same plate inside the frame's 1px hairline. A tool with no logo (an MCP server, a logo that never
 * loads) shows its initial.
 */
function ConnectorPlate({ node, common, label, ripple }: { node: MapNode; common: Record<string, unknown>; label: string; ripple: React.ReactNode }) {
  const { r, logo: box, wix, plus, plusStroke, plusBorder } = CANVAS_CONNECTORS;
  const slug = node.slug ?? '';
  const logo = node.action || slug.startsWith('mcp:') ? null : node.logoUrl ?? connectorLogoUrl(slug);
  const [logoFailed, setLogoFailed] = useState(false);
  // A new logo URL (a tool reconnected) gets its own chance to load.
  useEffect(() => setLogoFailed(false), [logo]);
  return (
    <g {...common} aria-label={label}>
      <g className={styles.fly}>
        {ripple}
        <g className={styles.body}>
          <circle r={r + 6} className={styles.halo} />
          <circle
            r={node.action === 'connect' ? r - plusBorder / 2 : r}
            className={`${styles.channelPlate}${node.action === 'connect' ? ` ${styles.connectPlate}` : ''}`}
            style={node.action === 'connect' ? { strokeWidth: plusBorder } : undefined}
          />
          {node.action === 'connect' ? (
            <svg x={-plus / 2} y={-plus / 2} width={plus} height={plus} viewBox="0 0 24 24" className={styles.channelGlyph}>
              <Plus size={24} strokeWidth={plusStroke} aria-hidden />
            </svg>
          ) : node.folded ? (
            <text y={5} textAnchor="middle" className={styles.tileText}>
              {node.label}
            </text>
          ) : slug === 'wix' ? (
            <svg x={-wix.w / 2} y={-wix.h / 2} width={wix.w} height={wix.h} viewBox="0 0 28.9271 11.1995" className={styles.channelGlyph}>
              <WixWordmark width={28.9271} />
            </svg>
          ) : logo && !logoFailed ? (
            <image
              href={logo}
              x={-box / 2}
              y={-box / 2}
              width={box}
              height={box}
              preserveAspectRatio="xMidYMid meet"
              onError={() => setLogoFailed(true)}
            />
          ) : (
            <text y={5} textAnchor="middle" className={styles.tileText}>
              {label.charAt(0).toUpperCase()}
            </text>
          )}
        </g>
      </g>
    </g>
  );
}

/** The glyph for a channel Symphony hears you through that has no brand logo. */
function channelGlyph(slug: string): React.ReactNode | null {
  if (slug === 'email-in') return <Mail size={24} aria-hidden />;
  if (slug === 'sms-in') return <MessageSquare size={24} aria-hidden />;
  if (slug === 'web') return <Globe size={24} aria-hidden />;
  if (slug === 'phone-app') return <Smartphone size={24} aria-hidden />;
  if (slug === 'symphony-apps') return <LayoutGrid size={24} aria-hidden />;
  if (slug.startsWith('coder:')) return <Bot size={24} aria-hidden />;
  return null;
}

/** A channel or tool: the genome's badge — a bright white plate with a hairline when connected, the recessed
 *  grey plate when not — carrying the logo (or a glyph). The name sits BESIDE a side-column tile (left of a
 *  channel in, right of an outreach channel — eight of them stack too tightly for labels underneath) and
 *  UNDER a tool in the bottom row. */
function TileNode({
  node,
  common,
  label,
  logo,
  glyph,
  ripple,
  badge,
}: {
  node: MapNode;
  common: Record<string, unknown>;
  label: string;
  logo: string | null;
  glyph?: React.ReactNode;
  ripple: React.ReactNode;
  badge?: number;
}) {
  const [logoFailed, setLogoFailed] = useState(false);
  // A new logo URL (a tool reconnected) gets its own chance to load.
  useEffect(() => setLogoFailed(false), [logo]);
  const side = node.region === 'channelsIn' ? 'start' : node.region === 'outreach' ? 'end' : 'under';
  const labelProps =
    side === 'start'
      ? { x: -NODE_R.small - 10, y: 4, textAnchor: 'end' as const }
      : side === 'end'
      ? { x: NODE_R.small + 10, y: 4, textAnchor: 'start' as const }
      : { x: 0, y: NODE_R.small + LABEL_GAP, textAnchor: 'middle' as const };
  const on = node.connected !== false;
  return (
    <g {...common} aria-label={label}>
      <g className={styles.fly}>
        {ripple}
        <g className={styles.body}>
          <rect
            x={-NODE_R.small - 6}
            y={-NODE_R.small - 6}
            width={(NODE_R.small + 6) * 2}
            height={(NODE_R.small + 6) * 2}
            rx={12}
            className={styles.halo}
          />
          <rect
            x={-NODE_R.small}
            y={-NODE_R.small}
            width={NODE_R.small * 2}
            height={NODE_R.small * 2}
            rx={9}
            className={on ? styles.plateOn : styles.plateOff}
          />
          {node.folded ? (
            <text y={5} textAnchor="middle" className={styles.tileText}>
              {node.label}
            </text>
          ) : glyph ? (
            <svg x={-9} y={-9} width={18} height={18} viewBox="0 0 24 24" className={styles.tileGlyph}>
              {glyph}
            </svg>
          ) : node.slug === 'wix' ? (
            <svg x={-11} y={-4.5} width={22} height={9} viewBox="0 0 28.9271 11.1995" className={styles.tileGlyph}>
              <WixWordmark width={28.9271} />
            </svg>
          ) : logo && !logoFailed ? (
            <image
              href={logo}
              x={-NODE_R.small + 5}
              y={-NODE_R.small + 5}
              width={NODE_R.small * 2 - 10}
              height={NODE_R.small * 2 - 10}
              clipPath="url(#home-map-logo-clip)"
              preserveAspectRatio="xMidYMid meet"
              // A logo the network never delivers (a blocked host, data saver) leaves a blank plate: fall
              // back to the tool's initial rather than an empty square.
              onError={() => setLogoFailed(true)}
            />
          ) : (
            <text y={5} textAnchor="middle" className={styles.tileText}>
              {node.label.slice(0, 1).toUpperCase()}
            </text>
          )}
        </g>
        <text {...labelProps} className={styles.nodeLabel}>
          {label}
        </text>
        {/* Replies waiting on the reader on this channel, on the tile's corner. */}
        {badge ? (
          <g transform={`translate(${NODE_R.small - 2} ${-NODE_R.small + 2})`} aria-hidden>
            <circle r={8} className={styles.nodeBadge} />
            <text y={3.5} textAnchor="middle" className={styles.nodeBadgeText}>
              {badge > 9 ? '9+' : badge}
            </text>
          </g>
        ) : null}
      </g>
    </g>
  );
}

function Minimap({ world, view, onMove, onBi }: { world: MapWorld; view: ViewBox; onMove: (p: { x: number; y: number }) => void; onBi: () => void }) {
  const { t } = useTranslation();
  const W = 132;
  const H = (W * WORLD_H) / WORLD_W;
  const dragging = useRef(false);
  const toWorldPoint = (e: React.PointerEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * WORLD_W, y: ((e.clientY - r.top) / r.height) * WORLD_H };
  };
  return (
    <svg
      className={styles.minimap}
      width={W}
      height={H}
      viewBox={`0 0 ${WORLD_W} ${WORLD_H}`}
      role="button"
      tabIndex={0}
      aria-label={t('home.map.minimap')}
      onPointerDown={(e) => {
        dragging.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
        onBi();
        onMove(toWorldPoint(e));
      }}
      onPointerMove={(e) => {
        if (dragging.current) onMove(toWorldPoint(e));
      }}
      onPointerUp={(e) => {
        dragging.current = false;
        e.currentTarget.releasePointerCapture(e.pointerId);
      }}
      onPointerCancel={() => {
        dragging.current = false;
      }}
    >
      <defs>
        <clipPath id="home-map-minimap-clip">
          <rect width={WORLD_W} height={WORLD_H} />
        </clipPath>
      </defs>
      <rect width={WORLD_W} height={WORLD_H} className={styles.minimapGround} />
      {/* No buckets: they are not drawn on the map either (the tiles are them). */}
      {world.nodes
        .filter((n) => n.region !== 'buckets')
        .map((n) => (
          <circle key={n.id} cx={n.x} cy={n.y} r={n.region === 'centre' ? 22 : 12} className={styles.minimapNode} />
        ))}
      <rect x={view.x} y={view.y} width={view.w} height={view.h} className={styles.minimapView} clipPath="url(#home-map-minimap-clip)" />
    </svg>
  );
}

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const update = () => setReduced(mq.matches);
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return reduced;
}

function iconForKind(kind: MapEvent['kind']) {
  switch (kind) {
    case 'file':
      return File;
    case 'task':
      return CircleCheckBig;
    case 'goal':
      return TrendingUp;
    case 'routine':
      return Repeat2;
    case 'app':
      return LayoutGrid;
    case 'outreach':
    case 'message':
    case 'notification':
      return Mail;
    default:
      return null;
  }
}

/** The label under a node: buckets and the connect-more node are localized; everything else says its name. */
export function nodeLabel(node: MapNode, t: (k: string) => string, maestroName?: string): string {
  if (node.region === 'buckets') return t(`home.map.bucket.${node.label}`);
  if (node.region === 'spaces') return node.label.length > 18 ? `${node.label.slice(0, 17)}…` : node.label;
  if (node.action === 'connect') return t('home.map.connectMore');
  if (node.id === MAESTRO_ID) return maestroName ?? t('home.map.maestro');
  if (node.id === BRIEF_ID) return t('home.map.brief');
  return shortLabel(node.label);
}

/** A node's name at map size: an MCP server that only has a URL for a name shows its host's first label;
 *  anything longer than ~14 characters is ellipsised (the card shows the full name). */
export function shortLabel(label: string): string {
  let text = label.trim();
  const url = /^(?:https?:\/\/)?([a-z0-9-]+)(?:\.[a-z0-9-]+)+/i.exec(text);
  if (url && text.includes('.') && !text.includes(' ')) text = url[1];
  return text.length > 14 ? `${text.slice(0, 13)}…` : text;
}
